import { ref } from 'vue'

export type ToastKind = 'success' | 'error' | 'info' | 'warning'

export interface ToastAction {
  label: string
  /** Devuelve false para mantener el toast abierto; cualquier otro valor lo cierra. */
  onClick: () => void | Promise<void | boolean>
}

export interface Toast {
  id: number
  kind: ToastKind
  message: string
  /** Botón opcional (p. ej. "Deshacer"). Si se pulsa, se cierra el toast. */
  action?: ToastAction
}

/**
 * Estado global (singleton a nivel de módulo) de los toasts.
 * Cualquier componente puede llamar a `useToaster()` para emitir
 * notificaciones; el componente <Toaster /> las renderiza.
 */
const toasts = ref<Toast[]>([])
let nextId = 0

/** Máximo de toasts visibles simultáneamente (FIFO: los más viejos se cierran). */
const MAX_TOASTS = 5

export function useToaster() {
  function push(
    message: string,
    kind: ToastKind = 'info',
    duration = 4000,
    action?: ToastAction,
  ) {
    const id = nextId++
    toasts.value.push({ id, kind, message, action })
    // Límite FIFO: si excedemos el máximo, descartamos los más viejos.
    if (toasts.value.length > MAX_TOASTS) {
      toasts.value = toasts.value.slice(toasts.value.length - MAX_TOASTS)
    }
    if (duration > 0) {
      setTimeout(() => dismiss(id), duration)
    }
    return id
  }

  function dismiss(id: number) {
    toasts.value = toasts.value.filter((t) => t.id !== id)
  }

  /** Ejecuta la acción del toast y lo cierra (salvo que devuelva false). */
  async function runAction(id: number) {
    const toast = toasts.value.find((t) => t.id === id)
    if (!toast?.action) return
    const keepOpen = await toast.action.onClick()
    if (keepOpen !== false) dismiss(id)
  }

  const success = (m: string, d?: number, action?: ToastAction) => push(m, 'success', d, action)
  const error = (m: string, d?: number, action?: ToastAction) => push(m, 'error', d ?? 6000, action)
  const warning = (m: string, d?: number, action?: ToastAction) => push(m, 'warning', d ?? 5000, action)
  const info = (m: string, d?: number, action?: ToastAction) => push(m, 'info', d, action)

  return { toasts, push, dismiss, runAction, success, error, warning, info }
}
